import React, {
	type PropsWithChildren,
	useContext,
	useEffect,
	useRef,
	useState,
	useCallback,
} from "react";
import type { MemberGateReason } from "../../components/MembershipGateContainer/MembershipGateContainer";
import { decrypt } from "../../utilities/crypto";
import { tryParseJson } from "../../utilities/stringUtils";
import { useMatomo } from "@datapunt/matomo-tracker-react";

declare global {
	interface Window {
		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
		dataLayer: any[];
		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
		_mtm: any[];
	}
}

const dataLayerObject = {
	event: "userAuthenticated",
};

// Define the structure of the consent cookie
interface ConsentCookie {
	googleconsentmap: {
		ad_storage: string;
		analytics_storage: string;
		ad_user_data: string;
		ad_personalization: string;
		functionality_storage: string;
		personalization_storage: string;
		security_storage: string;
	};
	firstpage: string;
	action?: string;
	consenttime?: number;
	categories: string | string[];
}

// Define the event detail structure for consent events
interface ConsentEventDetail {
	categories: string | string[];
	googleconsentmap?: {
		ad_storage?: string;
		analytics_storage?: string;
		ad_user_data?: string;
		ad_personalization?: string;
		functionality_storage?: string;
		personalization_storage?: string;
		security_storage?: string;
	};
}

// Extend the Event interface to include our custom detail
interface ConsentEvent extends Event {
	detail: ConsentEventDetail;
}

export interface EncryptResponse {
	key?: string;
	reason?: MemberGateReason;
}
interface AuthContextState {
	isUserLoggedIn: () => Promise<boolean>;
	loginOrMyAccount: () => void;
	logout: () => void;
	userId: string | null;

	getEncryptionKeyOrReason: (
		article: Queries.ArticleDataFragment | Queries.ArticleMidgateDataFragment,
	) => Promise<EncryptResponse>;
	decryptArticle: (
		key: string,
		article: Queries.ArticleDataFragment | Queries.ArticleMidgateDataFragment,
	) => Promise<false | EncryptedData>;
	parseNonEncryptedArticle: (
		article: Queries.ArticleDataFragment | Queries.ArticleMidgateDataFragment,
	) => EncryptedData;
	isLoggedIn: boolean;
}

const AuthContext = React.createContext<AuthContextState>(
	{} as AuthContextState,
);
const LOGGED_IN_TIMEOUT = 1000 * 60 * 30; // 5 minutes
export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const [userId, setUserId] = useState<string | null>(null);
	const loggedInTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	const matomoUrl = "https://rusi.matomo.cloud";
	const siteId = 1;
	useEffect(() => {
		var _mtm = (window._mtm = window._mtm || []);
		_mtm.push({ "mtm.startTime": new Date().getTime(), event: "mtm.Start" });
		(function () {
			var d = document,
				g = d.createElement("script"),
				s = d.getElementsByTagName("script")[0];
			g.async = true;
			g.src =
				"https://cdn.matomo.cloud/rusi.matomo.cloud/container_TUTNkNpL.js";
			s.parentNode.insertBefore(g, s);
		})();
	}, [siteId, matomoUrl]);

	/**
	 * Checks if the user is logged in.
	 * @returns Whether the user is logged in.
	 */
	const isUserLoggedIn = useCallback(async (): Promise<boolean> => {
		try {
			if (isLoggedIn) {
				return true;
			}
			if (process.env.GATSBY_LOGIN_AUTH_CHECK_URL) {
				// First we hit the my-account API to check if the user is logged in.
				const loginResponse = await fetch(
					process.env.GATSBY_LOGIN_AUTH_CHECK_URL,
					{
						credentials: "include",
						method: "GET",
						mode: "no-cors",
						redirect: "manual",
					},
				);

				loggedInTimeoutRef.current = setTimeout(() => {
					setIsLoggedIn(false);
					setUserId(null);
				}, LOGGED_IN_TIMEOUT);
				setIsLoggedIn(loginResponse.ok);
				if (loginResponse.ok) {
					const { userId: authUserId } = await loginResponse.json();
					setUserId(authUserId || null);

					if (typeof window !== "undefined") {
						let _paq = (window._paq = window._paq || []);
						_paq.push(["setUserId", authUserId]);
					}

					// Google Tag Manager
					window.dataLayer = window.dataLayer || [];
					window.dataLayer.push(dataLayerObject);

					localStorage.removeItem("redirectPage");
				}
				if (!loginResponse.ok) {
					setUserId(null);
				}
				return loginResponse.ok;
			}
			setIsLoggedIn(false);
			setUserId(null);
			return false;
		} catch (error) {
			console.error(error);
			setIsLoggedIn(false);
			setUserId(null);
			return false;
		}
	}, [isLoggedIn]);

	const logout = async () => {
		setUserId(null);
		setIsLoggedIn(false);
		localStorage.setItem("redirectPage", window.location.pathname + window.location.search + window.location.hash);

		const localLogoutUrl =
			process.env.GATSBY_MY_ACCOUNT_LOGOUT_URL || "/api/my-account/logout";
		const postLogoutUrl = process.env.GATSBY_LOGOUT_URL;

		// If both URLs are the same (or external logout is not configured),
		// navigate once to avoid a second auth challenge against the ALB.
		if (!postLogoutUrl || postLogoutUrl === localLogoutUrl) {
			window.location.href = localLogoutUrl;
			return;
		}

		try {
			await fetch(localLogoutUrl, {
				method: "GET",
				credentials: "include",
				cache: "no-store",
				redirect: "manual",
			});
		} catch (error) {
			console.error("Failed to clear ALB session during logout", error);
		}

		window.location.href = postLogoutUrl;
	};
	const loginOrMyAccount = () => {
		if (isLoggedIn) {
			window.location.href = `${process.env.GATSBY_MY_DASHBOARD_URL}`;
		} else {
			localStorage.setItem("redirectPage", window.location.pathname + window.location.search + window.location.hash);
			window.location.href =
				process.env.GATSBY_MY_ACCOUNT_URL || "/api/my-account/";
		}
	};

	useEffect(() => {
		if (typeof window !== "undefined") {
			window.addEventListener("CookieScriptLoaded", () => {
				consentSet();
			});

			window.addEventListener("CookieScriptAcceptAll", () => {
				consentSet();
			});

			window.addEventListener("CookieScriptAccept", () => {
				consentSet();
			});

			window.addEventListener("CookieScriptReject", () => {
				consentSet();
			});
		}

		function consentSet() {
			let _paq = (window._paq = window._paq || []);

			if (typeof window !== "undefined" && window.CookieScript?.instance) {
				const currentState = window.CookieScript.instance.currentState();
				if (
					currentState.categories?.includes("performance") &&
					currentState.action === "accept"
				) {
					_paq.push(["rememberCookieConsentGiven"]);
					_paq.push(["setCookieConsentGiven"]);
				} else {
					_paq.push(["forgetCookieConsentGiven"]);
					_paq.push(["deleteCookies"]);
				}
			}
		}

		return () => {
			// window.removeEventListener(
			// 	"CookieScriptConsentGiven",
			// 	consentGivenHandler,
			// );
			// window.removeEventListener(
			// 	"CookieScriptConsentRejected",
			// 	consentRejectedHandler,
			// );
		};
	}, []);

	useEffect(() => {
		const checkUserLoggedIn = async () => {
			const loggedIn = await isUserLoggedIn();
			setIsLoggedIn(loggedIn);
		};
		checkUserLoggedIn();

		return () => {
			if (loggedInTimeoutRef.current) {
				clearTimeout(loggedInTimeoutRef.current);
			}
		};
	}, []);

	/**
	 * Checks if the user can decrypt an article.
	 * @param article The article to check.
	 * @returns {key: string} The encryption key if the user can decrypt the article
	 * @returns {reason: string} A reason if the user cannot decrypt the article
	 */
	const getEncryptionKey = async (nodeId: string): Promise<EncryptResponse> => {
		try {
			if (!process.env.GATSBY_GATED_CONTENT_KEY_URL)
				return {
					reason: "error",
				};
			const response = await fetch(
				`${process.env.GATSBY_GATED_CONTENT_KEY_URL}/${nodeId}`,
				{
					credentials: "include",
					method: "GET",
					redirect: "manual",
				},
			);

			// Redirects/unauthorised responses should resolve to a gate reason,
			// not throw client-side parse errors.
			if (
				response.type === "opaqueredirect" ||
				[301, 302, 303, 307, 308, 401, 403].includes(response.status)
			) {
				return {
					reason: "no-account",
				};
			}

			if (!response.ok) {
				return {
					reason: "error",
				};
			}

			let res: { key?: string; reason?: MemberGateReason } | null = null;
			try {
				res = await response.json();
			} catch (err) {
				console.error("Failed to parse gated-content key response", err);
				return {
					reason: "error",
				};
			}

			// If we have the key it was a success
			if (res?.key) {
				return {
					key: res.key,
				};
			}
			// If we have a reason it was a failure
			return {
				reason: res?.reason,
			};
		} catch (error) {
			console.error(error);
			return {
				reason: "error",
			};
		}
	};

	function decryptEncryptedNode<T>(
		key: string,
		encryptedNode?: Queries.EncryptedNodeFragment,
		callback = (decrypted: string) => decrypted as T,
	) {
		if (!encryptedNode) return false;
		return callback(decrypt(key, encryptedNode.content));
	}

	/**
	 * 1. Checks if the user is logged in.
	 * 2. Checks if the user can get an encryption key for the article.
	 * 3. Validates the encryption key.
	 * 4. Returns either the encryption key or a reason why the user cannot decrypt the article.
	 * @param article - The article to check.
	 * @returns {key: string} The encryption key if the user can decrypt the article
	 * @returns {reason: string} A reason if the user cannot decrypt the article
	 */
	const getEncryptionKeyOrReason = async ({
		id,
		fields: { encrypted_validate, key: devKey },
	}:
		| Queries.ArticleDataFragment
		| Queries.ArticleMidgateDataFragment): Promise<EncryptResponse> => {
		if (devKey) return { key: devKey };

		// RUSI-1111 Always attempt key retrieval first. Backend decides whether request is
		// authorised (OIDC membership or trusted IP/header bypass).
		let { key, reason } = await getEncryptionKey(id);

		// If backend did not provide a key or reason, treat as an error rather than
		// assuming a login issue.
		if (!key && !reason) {
			reason = "error";
		}

		if (
			key &&
			decryptEncryptedNode<string>(key, encrypted_validate) !== "valid"
		) {
			key = undefined;
			reason = "error";
		}
		return {
			key,
			reason,
		};
	};

	/**
	 * Decrypts an article, given an encryption key.
	 * The key should have been pre-validated so this should always return the decrypted article data
	 * @param key The encryption key.
	 * @param article The article to decrypt.
	 * @returns false if the article could not be decrypted
	 * @returns EncryptedData if the article was decrypted
	 */
	const decryptArticle = async (
		key: string,
		article: Queries.ArticleDataFragment,
	): Promise<false | EncryptedData> => {
		try {
			const { fields, encryptedSections, encryptedPdfs } = article;
			const body = decryptEncryptedNode<string>(key, fields?.encrypted_body);

			const taylorAndFrancisUrl = decryptEncryptedNode<string>(
				key,
				fields?.encrypted_taylor_and_francis_url,
			);
			type ExcludesFalse = <T>(x: T | null) => x is T;
			const sections: ParagraphFragment[] = (
				encryptedSections
					?.map(
						(encryptedSection: Queries.EncryptedNodeFragment) =>
							decryptEncryptedNode<string>(key, encryptedSection) || null,
					)
					// biome-ignore lint/suspicious/noExplicitAny: Required to filter out nulls
					.filter(Boolean as any as ExcludesFalse) || []
			).map(tryParseJson);

			const pdfUrl: Queries.PdfDataFragment[] = (
				encryptedPdfs
					?.map(
						(pdf: Queries.EncryptedNodeFragment) =>
							decryptEncryptedNode<string>(key, pdf) || null,
					)
					// biome-ignore lint/suspicious/noExplicitAny: Required to filter out nulls
					.filter(Boolean as any as ExcludesFalse) || []
			).map(tryParseJson);

			return {
				body,
				pdfUrl,
				taylorAndFrancisUrl,
				sections,
			};
		} catch (error) {
			console.log("error", error);
			return false;
		}
	};

	const parseNonEncryptedArticle = (
		article: Queries.ArticleDataFragment,
	): EncryptedData => {
		const contentOrFalse = (content?: string) =>
			content && content.length > 0 ? content : false;

		const { fields, encryptedSections, encryptedPdfs } = article;

		const sections: ParagraphFragment[] =
			encryptedSections
				?.filter(
					(
						section: Queries.EncryptedNodeFragment,
					): section is typeof section & { content: string } =>
						Boolean(section?.content),
				)
				.map((section: Queries.EncryptedNodeFragment) =>
					tryParseJson(section.content),
				) || [];

		const pdfUrl: Queries.PdfDataFragment[] =
			encryptedPdfs
				?.filter(
					(
						section: Queries.EncryptedNodeFragment,
					): section is typeof section & { content: string } =>
						Boolean(section?.content),
				)
				.map((section: Queries.EncryptedNodeFragment) =>
					tryParseJson(section.content),
				) || [];

		return {
			body: contentOrFalse(fields?.encrypted_body?.content),
			pdfUrl,
			taylorAndFrancisUrl: contentOrFalse(
				fields?.encrypted_taylor_and_francis_url?.content,
			),
			sections,
		};
	};
	return (
		<AuthContext.Provider
			value={{
				isUserLoggedIn,
				loginOrMyAccount,
				logout,
				userId,
				getEncryptionKeyOrReason,
				decryptArticle,
				parseNonEncryptedArticle,
				isLoggedIn,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = (): AuthContextState => useContext(AuthContext);
